<script lang="ts">
  import { createContext } from 'reka-ui'
  export interface FormValidationResult {
    valid: boolean
    errors: Partial<Record<any, string>>
    values?: Partial<any>
    source: 'fields'
  }

  export interface ValidateOptions {
    onlyDirty?: boolean
  }

  export interface GatFormProps {
    formName?: string
    readonly?: boolean
    loading?: boolean
    fieldType?: 'default' | 'inset'
  }

  function defineGatFormContext(
    props: GatFormProps & { isProvided?: boolean }
  ): FormContext<any> {
    // Form context implementation
    const registeredFields: Ref<Map<string, FormField<any>>> = ref(new Map())

    const isProvided = props.isProvided ?? true
    const fieldType = props.fieldType ?? 'default'
    const readonly = props.readonly ?? false
    const formName = props.formName
    const registerField = (field: FormField<any>) => {
      registeredFields.value.set(field.id, field)
    }
    const unregisterField = (id: string) => {
      registeredFields.value.delete(id)
    }

    const validateAllFields = async (options?: { onlyDirty?: boolean }) => {
      if (!isProvided) {
        console.error('GatForm was not found/provided')
        return { valid: false, errors: {}, source: 'not-provided' }
      }
      const errors: Record<string, string> = {}
      let allValid = true

      for (const field of registeredFields.value.values()) {
        // Skip validation if onlyDirty is true and field is not dirty
        if (options?.onlyDirty && !field.isDirty) {
          continue
        }

        try {
          const isValid = await field.validate(field.value)
          if (!isValid) {
            allValid = false
            errors[field.name] = 'Validation failed'
          }
        } catch (error) {
          allValid = false
          errors[field.name] =
            error instanceof Error ? error.message : 'Validation error'
        }
      }

      const firstErrorField = Object.keys(errors)[0]
      if (firstErrorField) {
        document.getElementById(firstErrorField)?.focus()
      }

      return { valid: allValid, errors }
    }

    const resetAllFields = () => {
      for (const field of registeredFields.value.values()) {
        if (field.reset) {
          field.reset()
        }
        // Reset dirty state
        if (field.markDirty) {
          field.isDirty = false
        }
      }
    }

    const getField = (id: string) => {
      return registeredFields.value.get(id)
    }

    const getAllFields = () => {
      return Array.from(registeredFields.value.values())
    }

    const markFieldDirty = (id: string) => {
      const field = registeredFields.value.get(id)
      if (field?.markDirty) {
        field.markDirty()
      }
    }

    const _errorMessage = ref<string>()

    function setErrorMessage(errorMessage: string) {
      _errorMessage.value = errorMessage
    }

    async function validate(
      options?: ValidateOptions
    ): Promise<FormValidationResult> {
      // Then validate all registered fields
      const fieldsValidationResult = await validateAllFields(options)

      // Combine results
      const combinedValid = fieldsValidationResult.valid
      const combinedErrors = {
        ...fieldsValidationResult.errors
      }
      if (!combinedValid) {
        setErrorMessage('Please fill in all required fields')
      }

      return {
        valid: combinedValid,
        errors: fieldsValidationResult.errors,
        source: 'fields'
      }
    }

    function resetValidation() {
      setTimeout(() => {
        resetAllFields()
      })
    }

    function setAiValues(values: Record<string, any>) {
      for (const [fieldId, field] of registeredFields.value.entries()) {
        const found = values[field.aiExtractPropertyName ?? field.name]
        if (found) {
          field.setAiValue(found.value, found.inferenceInfo ?? undefined)
        }
      }
    }

    return {
      isProvided,
      fieldType,
      readonly,
      formName,
      errorMessage: computed(() => _errorMessage.value),
      getField,
      getAllFields,
      markFieldDirty,
      validate,
      resetValidation,
      registerField,
      unregisterField,
      validateAllFields,
      resetAllFields,
      setAiValues,
      setErrorMessage
    }
  }

  const [_useGatForm, provideFormContext] = createContext<FormContext<any>>(
    GAT_FORM_CONTEXT_KEY.toString()
  )

  export function useGatForm(): FormContext<any> {
    try {
      return _useGatForm()
    } catch (error) {
      return defineGatFormContext({
        isProvided: false,
        fieldType: 'default'
      })
    }
  }
</script>

<script setup lang="ts">
  import { computed, onMounted, ref, type Ref } from 'vue'
  import { GAT_FORM_CONTEXT_KEY, type FormContext } from './injection-keys'
  import type { FormField } from '../gat-form-field/useFormField'

  const props = withDefaults(defineProps<GatFormProps>(), {
    fieldType: 'inset'
  })

  const formContext = defineGatFormContext(props)

  provideFormContext(formContext)

  onMounted(() => {
    formContext.resetValidation()
  })

  defineExpose(formContext)
</script>
<template>
  <slot />
</template>
